import React, { useState, useEffect } from 'react';
import './styles.scss';
import { Button, Image, Form, Offcanvas } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { formatTime } from 'utils';
import { useGetJobAISuggestionsQuery } from 'services';
import useTranslate from 'hooks/useTranslate';
import { setIsLoading } from 'store/common';

interface JObAiSuggestionsProps {
	onOpenAIResponse: (data: any) => void; // Function to handle API response in the parent component
	updateSalaryFunc: any;
}

export const JobAiSuggestions: React.FC<JObAiSuggestionsProps> = ({
	onOpenAIResponse,
	updateSalaryFunc,
}) => {
	const { translate } = useTranslate();
	const questions = [
		translate('Job.aiQuestion1'),
		translate('Job.aiQuestion2'),
		translate('Job.aiQuestion3'),
		translate('Job.aiQuestion4'),
	];

	const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
	const [chatHistory, setChatHistory] = useState<
		{ question: string; answer: string; questionTime: string; answerTime: string }[]
	>([]);
	const [currentInput, setCurrentInput] = useState<string>(''); // State to manage the current input value
	const [isFinished, setIsFinished] = useState<boolean>(false); // Track if the chat is finished
	const [questionTime, setQuestionTime] = useState<string>(new Date().toISOString()); // Track when the current question was asked
	const [ans, setAns] = useState('');
	const [payload, setPayload] = useState({ jobSummary: '' });
	const { data, refetch } = useGetJobAISuggestionsQuery(payload);

	// Update question time whenever a new question is presented
	useEffect(() => {
		if (currentQuestionIndex < questions.length) {
			setQuestionTime(new Date().toISOString());
		}
	}, [currentQuestionIndex]);

	useEffect(() => {
		if (isFinished && payload) {
			refetch(); // Refetches data when conditions are met
		}
	}, [isFinished, payload, refetch]);

	// Handle the API response and send it to the parent component
	useEffect(() => {
		if (data && isFinished) {
			onOpenAIResponse(data); // Send data to parent when available
			setIsLoading(false);
			resetChat(); // Reset chat after processing the responses
		}
	}, [data]);

	// Function to reset the chat
	const resetChat = () => {
		setCurrentQuestionIndex(0); // Reset question index
		setChatHistory([]); // Clear chat history
		setCurrentInput(''); // Clear input field
		setIsFinished(false); // Reset finished state
		setShow(false); // Close chat window
	};

	// Handle answer submission
	const handleSend = () => {
		if (currentInput.trim()) {
			setAns((prev) => `${prev} ${currentInput}`);
			const answerTime = new Date().toISOString(); // Get the current time as an ISO string for the answer

			// Add the question, answer, and their respective timestamps to chatHistory
			setChatHistory([
				...chatHistory,
				{
					question: questions[currentQuestionIndex],
					answer: currentInput,
					questionTime: questionTime, // Use the previously set question timestamp
					answerTime: answerTime, // Use the new timestamp for the answer
				},
			]);

			setCurrentInput(''); // Clear input field

			if (currentQuestionIndex < questions.length - 1) {
				setCurrentQuestionIndex(currentQuestionIndex + 1); // Move to the next question
			} else {
				setIsLoading(true);
				updateSalaryFunc(currentInput);
				setPayload({ jobSummary: `${ans} ${currentInput}` }); // Set payload data
				setIsFinished(true); // Mark the chat as finished
			}
		}
	};

	const [show, setShow] = useState(false);

	const handleClose = () => setShow(false);
	const handleShow = () => setShow(true);

	return (
		<section className="job-ai-suggestion">
			<Button
				aria-controls="example-collapse-text"
				aria-expanded={open}
				className="border-0 job-ai-suggestion-btn p-0 rounded-circle"
				onClick={handleShow}
			>
				<i className="ri-chat-3-fill"></i>
				<span className="ai-tooltip-text">{translate('Resume.aiSsuggestions')}</span>
			</Button>

			<Offcanvas show={show} placement="end">
				<div className="chat-collapse">
					<div id="example-collapse-text">
						<div className="ai-chat-head d-flex align-items-center gap-2 gap-sm-3">
							<div className="d-flex align-items-center gap-2 gap-sm-3 flex-1">
								<div className="ai-chat-head-img">
									<Image src={IMAGE_PATHS.chatbotIcon} loading="lazy" />
								</div>
								<h5 className="mb-0 fw-bold">{translate('Job.chatbot')}</h5>
							</div>
							<Button
								onClick={() => handleClose()}
								className="job-ai-suggestion-close-btn p-0 rounded-circle border-0"
							>
								<i className="ri-close-line"></i>
							</Button>
						</div>

						<div className="ai-chat-body">
							{chatHistory.map((chat, index) => (
								<div key={index}>
									<div className="received-chat">
										<div className="ai-chat-msg">
											<p className="mb-0">
												{chat.question}{' '}
												<span className="ai-chat-msg-time">{formatTime(chat.questionTime)}</span>
											</p>
										</div>
									</div>
									<div className="sent-chat">
										<div className="ai-chat-msg sent">
											<p className="mb-0">
												{chat.answer}{' '}
												<span className="ai-chat-msg-time">{formatTime(chat.answerTime)}</span>
											</p>
										</div>
									</div>
								</div>
							))}

							{!isFinished && (
								<div className="received-chat">
									<div className="ai-chat-msg">
										<p className="mb-0">
											{questions[currentQuestionIndex]}{' '}
											<span className="ai-chat-msg-time">{formatTime(questionTime)}</span>
										</p>
									</div>
								</div>
							)}

							{isFinished && (
								<div className="received-chat">
									<div className="ai-chat-msg">
										<p className="mb-0">
											{translate('Job.chatBotThankYou')}
											<span className="ai-chat-msg-time">
												{formatTime(new Date().toISOString())}
											</span>
										</p>
									</div>
								</div>
							)}
						</div>

						{/* Input box */}
						<div className="ai-chat-bottom d-flex align-items-center gap-2 gap-sm-3">
							<div className="flex-1">
								<Form.Control
									placeholder={translate('Job.typeYourAnswer')}
									aria-label="Answer"
									value={currentInput}
									onChange={(e) => setCurrentInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											handleSend();
										}
									}}
									className="rounded-pill"
									disabled={isFinished}
								/>
							</div>
							<Button
								className="filled-btn rounded-circle p-0"
								onClick={handleSend}
								disabled={isFinished}
							>
								<i className="ri-send-plane-fill"></i>
							</Button>
						</div>
					</div>
				</div>
			</Offcanvas>
		</section>
	);
};
